/**
 * Google Maps API Loader Utility
 * Handles loading and initialization of Google Maps JavaScript API
 */

// Configuration - Replace with your actual Google Maps API key
const GOOGLE_MAPS_CONFIG = {
  apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE',
  libraries: ['places', 'geometry'],
  version: 'weekly',
  language: 'en',
  region: 'US'
};

let isLoading = false;
let isLoaded = false;
let loadPromise = null;

/**
 * Load Google Maps JavaScript API
 * @returns {Promise} Promise that resolves when API is loaded
 */
export const loadGoogleMapsAPI = () => {
  // Return existing promise if already loading
  if (loadPromise) {
    return loadPromise;
  }

  // Return resolved promise if already loaded
  if (isLoaded) {
    return Promise.resolve();
  }

  // Check if API key is configured
  if (!GOOGLE_MAPS_CONFIG.apiKey || GOOGLE_MAPS_CONFIG.apiKey === 'YOUR_API_KEY_HERE') {
    return Promise.reject(new Error('Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.'));
  }

  isLoading = true;

  loadPromise = new Promise((resolve, reject) => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      isLoaded = true;
      isLoading = false;
      resolve();
      return;
    }

    // Create callback function name
    const callbackName = 'initGoogleMaps';
    
    // Create global callback
    window[callbackName] = () => {
      isLoaded = true;
      isLoading = false;
      delete window[callbackName];
      resolve();
    };

    // Create script element
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    
    // Build API URL
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_CONFIG.apiKey,
      libraries: GOOGLE_MAPS_CONFIG.libraries.join(','),
      v: GOOGLE_MAPS_CONFIG.version,
      language: GOOGLE_MAPS_CONFIG.language,
      region: GOOGLE_MAPS_CONFIG.region,
      callback: callbackName
    });
    
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    
    // Handle script load errors
    script.onerror = () => {
      isLoading = false;
      delete window[callbackName];
      reject(new Error('Failed to load Google Maps API'));
    };

    // Add script to document
    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Check if Google Maps API is loaded
 * @returns {boolean} True if API is loaded
 */
export const isGoogleMapsLoaded = () => {
  return isLoaded && window.google && window.google.maps && window.google.maps.places;
};

/**
 * Get Google Maps API loading status
 * @returns {object} Loading status object
 */
export const getLoadingStatus = () => {
  return {
    isLoading,
    isLoaded,
    isAvailable: isGoogleMapsLoaded()
  };
};

/**
 * Geocode an address using Google Maps Geocoding API
 * @param {string} address - Address to geocode
 * @returns {Promise} Promise that resolves with geocoding results
 */
export const geocodeAddress = async (address) => {
  if (!isGoogleMapsLoaded()) {
    throw new Error('Google Maps API not loaded');
  }

  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const result = results[0];
        resolve({
          address: result.formatted_address,
          coordinates: {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng()
          },
          addressComponents: result.address_components,
          placeId: result.place_id,
          types: result.types
        });
      } else {
        reject(new Error(`Geocoding failed: ${status}`));
      }
    });
  });
};

/**
 * Reverse geocode coordinates to get address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string>} - Address string
 */
export const reverseGeocode = async (lat, lng) => {
  await loadGoogleMapsAPI();
  
  const geocoder = new window.google.maps.Geocoder();
  const latlng = { lat, lng };
  
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        resolve(results[0].formatted_address);
      } else {
        reject(new Error(`Reverse geocoding failed: ${status}`));
      }
    });
  });
};

/**
 * Search for nearby emergency services using Google Places API
 * @param {string} location - Location to search near (address or coordinates)
 * @param {string} serviceType - Type of service ('hospital', 'police', 'fire_station', 'ambulance')
 * @param {number} radius - Search radius in meters (default: 10000 = 10km)
 * @returns {Promise<Array>} - Array of emergency service places
 */
export const searchEmergencyServices = async (location, serviceType, radius = 10000) => {
  // Validate required parameters
  if (!location) {
    throw new Error('Missing parameter. You must specify location.');
  }
  if (!serviceType) {
    throw new Error('Missing parameter. You must specify serviceType.');
  }
  
  await loadGoogleMapsAPI();
  
  // First, geocode the location if it's an address
  let searchLocation;
  if (typeof location === 'string') {
    try {
      const geocodeResult = await geocodeAddress(location);
      // Convert coordinates to Google Maps LatLng object
      searchLocation = new window.google.maps.LatLng(
        geocodeResult.coordinates.lat,
        geocodeResult.coordinates.lng
      );
    } catch (error) {
      throw new Error(`Failed to geocode location: ${error.message}`);
    }
  } else if (location.lat && location.lng) {
    // If location is already coordinates, convert to LatLng object
    searchLocation = new window.google.maps.LatLng(location.lat, location.lng);
  } else {
    throw new Error('Invalid location format. Expected address string or coordinates object.');
  }

  // Map service types to Google Places types
  const serviceTypeMap = {
    hospital: ['hospital', 'doctor', 'health'],
    police: ['police'],
    fire_station: ['fire_station'],
    ambulance: ['hospital', 'doctor'] // Ambulance services are often associated with hospitals
  };

  const placeTypes = serviceTypeMap[serviceType] || ['hospital'];
  
  return new Promise((resolve, reject) => {
    const map = new window.google.maps.Map(document.createElement('div'));
    const service = new window.google.maps.places.PlacesService(map);
    
    const request = {
      location: searchLocation,
      radius: radius,
      type: placeTypes[0] // nearbySearch expects a single type, not an array
    };

    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        // Filter and format results
        const formattedResults = results
          .filter(place => {
            // Additional filtering based on service type
            if (serviceType === 'police') {
              return place.types.includes('police') || 
                     place.name.toLowerCase().includes('police') ||
                     place.name.toLowerCase().includes('sheriff');
            }
            if (serviceType === 'fire_station') {
              return place.types.includes('fire_station') || 
                     place.name.toLowerCase().includes('fire');
            }
            if (serviceType === 'hospital') {
              return place.types.includes('hospital') || 
                     place.types.includes('doctor') ||
                     place.name.toLowerCase().includes('hospital') ||
                     place.name.toLowerCase().includes('medical') ||
                     place.name.toLowerCase().includes('clinic');
            }
            if (serviceType === 'ambulance') {
              return place.types.includes('hospital') || 
                     place.name.toLowerCase().includes('ambulance') ||
                     place.name.toLowerCase().includes('emergency') ||
                     place.name.toLowerCase().includes('ems');
            }
            return true;
          })
          .map(place => ({
            id: place.place_id,
            name: place.name,
            address: place.formatted_address || place.vicinity,
            phoneNumber: place.formatted_phone_number || 'Call 911 for emergencies',
            website: place.website,
            rating: place.rating,
            userRatingsTotal: place.user_ratings_total,
            location: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            },
            distance: calculateDistance(
              searchLocation.lat,
              searchLocation.lng,
              place.geometry.location.lat(),
              place.geometry.location.lng()
            ),
            openingHours: place.opening_hours,
            types: place.types,
            placeId: place.place_id
          }))
          .sort((a, b) => a.distance - b.distance) // Sort by distance
          .slice(0, 10); // Limit to top 10 results

        resolve(formattedResults);
      } else {
        reject(new Error(`Places search failed: ${status}`));
      }
    });
  });
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} - Distance in kilometers
 */
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

/**
 * Get detailed information about a specific place
 * @param {string} placeId - Google Places ID
 * @returns {Promise<Object>} - Detailed place information
 */
export const getPlaceDetails = async (placeId) => {
  await loadGoogleMapsAPI();
  
  return new Promise((resolve, reject) => {
    const map = new window.google.maps.Map(document.createElement('div'));
    const service = new window.google.maps.places.PlacesService(map);
    
    const request = {
      placeId: placeId,
      fields: [
        'place_id',
        'name',
        'formatted_address',
        'geometry',
        'rating',
        'user_ratings_total',
        'formatted_phone_number',
        'international_phone_number',
        'website',
        'opening_hours',
        'types',
        'reviews'
      ]
    };

    service.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        resolve({
          id: place.place_id,
          name: place.name,
          address: place.formatted_address,
          phoneNumber: place.formatted_phone_number || place.international_phone_number,
          website: place.website,
          rating: place.rating,
          userRatingsTotal: place.user_ratings_total,
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          },
          openingHours: place.opening_hours,
          types: place.types,
          reviews: place.reviews,
          placeId: place.place_id
        });
      } else {
        reject(new Error(`Place details failed: ${status}`));
      }
    });
  });
};

export default {
  loadGoogleMapsAPI,
  isGoogleMapsLoaded,
  getLoadingStatus,
  geocodeAddress,
  reverseGeocode,
  searchEmergencyServices,
  getPlaceDetails
};
